import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { tap, switchMap, map } from 'rxjs/operators';
import * as moment from 'moment';
import { ConfigurationService, IAppSettings } from '../../../services/configuration/configuration.service';
import { OirHttpService } from '../../../services/http/oir-http.service';
import { NotificationMessage } from '../notifications.model';
import { NotificationsTrayModel } from './notifications-tray.model';

@Injectable({ providedIn: 'root' })
export class NotificationsTrayService {
  trayModel = new BehaviorSubject<NotificationsTrayModel>({
    hidden: false,
    expanded: false,
    items: []
  });

  constructor(
    private readonly configuration: ConfigurationService<IAppSettings>,
    private readonly oirHttp: OirHttpService
  ) {}

  isActive(): boolean {
    return this.trayModel.getValue().expanded;
  }

  toggle(): void {
    const value = this.trayModel.getValue();
    if (value.expanded) {
      value.expanded = false;
      value.hidden = true;
    } else {
      value.hidden = false;
      value.expanded = true;
    }
    this.trayModel.next(value);
  }

  getNotifications(): Observable<NotificationsTrayModel> {
    return this.configuration.get('UserName').pipe(
      switchMap((userName) =>
        this.oirHttp.get({
          requestUrl: 'CommsEndpoint',
          path: '/api/Notifications',
          query: { userName },
          hideSpinner: true
        })
      ),
      map((data: NotificationMessage[]) => {
        data.forEach((v: NotificationMessage) => {
          v.read = v.status === 'Actioned';
          v.creationDate = v.creationDate
            ? moment(v.creationDate).format('DD/MM/YYYY HH:mm')
            : (v.creationDate = null);
        });
        return data;
      }),
      switchMap((res) => {
        return of({
          expanded: false,
          hidden: true,
          items: res,
          unread: res.filter((x) => !x.read).length,
          moduleName: this.configuration.getValue('ModuleName'),
        } as NotificationsTrayModel);
      }),
      tap(v => this.trayModel.next(v))
    );
  }

  addNotification(message: NotificationMessage): void {
    message.dismissed = true;
    message.read = message.status === 'Actioned';

    const value = this.trayModel.getValue();
    value.items.unshift(message);
    this.trayModel.next(value);

    setTimeout(() => {
      message.dismissed = false;

      const value = this.trayModel.getValue();
      value.unread = value.items.filter(x => !x.read).length;
      this.trayModel.next(value);
    }, 200);
  }

  dismiss(message: NotificationMessage): Observable<any> {
    const body = [{ id: message.id, userName: message.userName }];

    return this.oirHttp
      .delete({
        requestUrl: 'CommsEndpoint',
        path: '/api/Notifications',
        body,
      })
      .pipe(
        tap(() => {
          const value = this.trayModel.getValue();
          value.items = value.items.filter((x) => x !== message);
          value.unread = value.items.filter((x) => !x.read).length;
          this.trayModel.next(value);
          message.dismissed = true;
        })
      );
  }

  dismissAll(): Observable<any> {
    const body = [];
    const value = this.trayModel.getValue();
    for (let i = 0; i < value.items.length; i++) {
      const item = value.items[i];
      body.push({
        id: item.id,
        userName: this.configuration.getValue('UserName'),
      } as any);
    }

    return this.oirHttp
      .delete({
        requestUrl: 'CommsEndpoint',
        path: '/api/Notifications',
        body,
      })
      .pipe(
        tap(() => {
          const value = this.trayModel.getValue();
          value.items = [];
          value.unread = 0;
          this.trayModel.next(value);
        })
      );
  }

  read(message: NotificationMessage): Observable<any> {
    const body = [{ id: message.id, userName: message.userName }];

    return this.oirHttp
      .delete({
        requestUrl: 'CommsEndpoint',
        path: '/api/Notifications',
        body,
      })
      .pipe(
        tap(() => {
          message.read = true;

          const value = this.trayModel.getValue();
          value.unread = value.items.filter((x) => !x.read).length;
          this.trayModel.next(value);
        })
      );
  }
}
